import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {withTranslation} from 'react-i18next';
import DatatableLoadMore from '../../../../common/components/Datatable/DatatableLoadMore';
import gql from 'graphql-tag';
import DisplayAmount from '../../../../common/components/DisplayAmount';
import {dateFormatFR} from '../../../../utils';
import DisplayInvoiceStatus from '../../../billing/components/DisplayInvoiceStatus';
import {INVOICE_STATUS} from '../../../../common/constants';
import FlexRow from '../../../../common/components/layout/FlexRow';
import DownloadInvoice from '../../../billing/components/DownloadInvoice';
import SelectInvoicesStatus from '../../../billing/components/SelectInvoicesStatus';
import {MargedDiv} from '../../../../common/components/Datatable/DatatableStyles';
import {Col, Container, Row} from 'react-bootstrap';
import SubscriptionManagement from '../../../stripe/components/SubscriptionManagement';
import PaymentMethodManagement from '../../../stripe/components/PaymentMethodManagement';
import {useQuery} from 'react-apollo';
import {PulseLoader} from 'react-spinners';
import {ContentDiv, HeadDiv} from '../../../../common/components/StyledComp';

const GRAPHQL_QUERY_MERCHANT_INVOICES = gql`
  query merchantInvoices($criteria: CriteriaListInput, $additionalCriteria: InvoicesCriteriaListInput) {
    invoices(criteria: $criteria, additionalCriteria: $additionalCriteria) {
      rows{
        id
        amountDue
        amountPaid
        amountRemaining
        created
        currency
        status
        invoicePdf
        paid
        total
        tax
      }
      id
      hasMore
    }
  }
`;

const GRAPHQL_QUERY_MERCHANT = gql`
  query merchant($id: ID!) {
    merchant(id: $id) {
      id
      currentBillingSubscriptionUsageRecordSummary {
        id
        totalUsage
      }
      user {
        id
      }
    }
  }
`;

const columns = t => [
  {
    Header: t('date'),
    accessor: 'created',
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
  },
  {
    Header: t('status.title'),
    accessor: 'status',
    Cell: ({cell}) => <DisplayInvoiceStatus status={cell.value} />,
  },
  {
    Header: t('amountDue'),
    accessor: 'total',
    Cell: ({cell}) => <DisplayAmount amount={cell.value}/>,
  },
  {
    Header: t('action'),
    Cell: ({cell}) => (
      <FlexRow>
        {cell.value.status !== INVOICE_STATUS.DRAFT ? <DownloadInvoice invoicePdf={cell.value.invoicePdf}/> : null}
      </FlexRow>
    ),
    accessor: row => row,
  },
];

const createAdditionalCriteria = (merchantId, statusFilter) => {
  const criteria = {
    merchantId,
  };
  if (Object.values(INVOICE_STATUS).includes(statusFilter)) {
    criteria.status = statusFilter;
  }
  return criteria;
};

const MerchantBillingDetails = ({t, merchantId}) => {
  const [statusFilter, setStatusFilter] = useState('default');
  const {loading, error, data} = useQuery(GRAPHQL_QUERY_MERCHANT, {
    variables: {id: merchantId},
  });
  if (error) {
    console.error(error);
  }

  const additionalFilters = (
    <MargedDiv>
      <SelectInvoicesStatus value={statusFilter} allowNull onChange={e => setStatusFilter(e.target.value)}/>
    </MargedDiv>
  );

  const totalUsage =
    data &&
    data.merchant &&
    data.merchant.currentBillingSubscriptionUsageRecordSummary &&
    data.merchant.currentBillingSubscriptionUsageRecordSummary.totalUsage ?
      data.merchant.currentBillingSubscriptionUsageRecordSummary.totalUsage : 0;


  return (
    <>
      <Container className="general-container">
        <Row className="info-row">
          <Col xs={6}>
            <div className="section-info-user">
              <HeadDiv className="info-header-merchant">{`${t('subscription')} : `}</HeadDiv>
              <ContentDiv>
                {(loading || error) ? (
                  <PulseLoader/>
                ) : (
                  <SubscriptionManagement userId={data.merchant.user.id} allowCancelSubscription={true} currentUsage={totalUsage} />
                )}
              </ContentDiv>
            </div>
          </Col>
          <Col xs={6}>
            <div className="section-info-user">
              <HeadDiv className="info-header-merchant">{`${t('payment')} : `}</HeadDiv>
              <ContentDiv>
                {(loading || error) ? (
                  <PulseLoader/>
                ) : (
                  <PaymentMethodManagement userId={data.merchant.user.id}/>
                )}
              </ContentDiv>
            </div>
          </Col>
        </Row>
      </Container>

      <DatatableLoadMore
        query={GRAPHQL_QUERY_MERCHANT_INVOICES}
        keyResult="invoices"
        columns={columns(t)}
        additionalCriteria={createAdditionalCriteria(merchantId, statusFilter)}
        additionalFilters={additionalFilters}
      />
    </>
  );
};

MerchantBillingDetails.propTypes = {
  t: PropTypes.func.isRequired,
  merchantId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default compose(
  withTranslation('merchant'),
)(MerchantBillingDetails);
